/**
 * Signup.tsx — Multi-step onboarding wizard container.
 *
 * Steps: Plan → Account → Cloud → Scan → Done
 * Progress is persisted via URL: /onboarding/:session_id
 * Users can close and resume.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import SecurityIcon from '@mui/icons-material/Security';
import axios from 'axios';
import API_BASE from '../../config/api';

import StepPlan, { Plan } from './steps/StepPlan';
import StepAccount from './steps/StepAccount';
import StepCloud from './steps/StepCloud';
import StepAutoFix from './steps/StepAutoFix';
import StepPayment from './steps/StepPayment';
import StepScan from './steps/StepScan';
import StepComplete from './steps/StepComplete';

// Step indices
// 0 Plan | 1 Account | 2 Cloud | 3 AutoFix (optional) | 4 Payment | 5 Scan | 6 Done
const STEP_LABELS = ['Choose Plan', 'Create Account', 'Connect Cloud', 'Auto-Fix', 'Payment', 'First Scan', 'Done'];

export default function Signup() {
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const VALID_PLANS: Plan[] = ['starter', 'growth', 'professional'];
  const urlPlan = searchParams.get('plan') as Plan | null;
  const initialPlan: Plan = urlPlan && VALID_PLANS.includes(urlPlan) ? urlPlan : 'growth';

  const [activeStep, setActiveStep]       = useState(0);
  const [plan, setPlan]                   = useState<Plan>(initialPlan);
  const [sessionId, setSessionId]         = useState<string>(urlSessionId ?? '');
  const [accessToken, setAccessToken]     = useState('');
  const [userId, setUserId]               = useState('');
  const [customerId, setCustomerId]       = useState('');
  const [connectedProvider, setConnectedProvider] = useState('');
  const [riskScore, setRiskScore]         = useState(0);
  const [findingCount, setFindingCount]   = useState(0);
  const [trialDays, setTrialDays]         = useState(14);
  const [planPrice, setPlanPrice]         = useState(0);
  const [loading, setLoading]             = useState(!!urlSessionId);

  // Resume session from URL
  useEffect(() => {
    if (!urlSessionId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/onboarding/${urlSessionId}/status`);
        setSessionId(urlSessionId);
        setPlan(data.plan ?? 'growth');
        if (data.customer_id) setCustomerId(data.customer_id);
        if (data.user_id)     setUserId(data.user_id);
        // Map server step name → wizard index
        const stepMap: Record<string, number> = {
          plan_selection:   0,
          account_creation: 1,
          cloud_connection: 2,
          first_scan:       5,
          complete:         6,
        };
        setActiveStep(stepMap[data.step] ?? 0);
      } catch { /* session not found — start fresh */ }
      setLoading(false);
    })();
  }, [urlSessionId]);

  async function handlePlanNext() {
    // Fetch trial_days for selected plan
    try {
      const { data: planData } = await axios.get(`${API_BASE}/plans/${plan}`);
      if (planData.trial_days) setTrialDays(planData.trial_days);
      if (planData.price_monthly) setPlanPrice(planData.price_monthly);
    } catch { /* keep default 14 */ }
    // Start session on backend
    const { data } = await axios.post(`${API_BASE}/onboarding/start`, {
      email: 'pending@shield.local', // real email collected in next step
      plan,
    });
    setSessionId(data.session_id);
    navigate(`/onboarding/${data.session_id}`, { replace: true });
    setActiveStep(1);
  }

  function handleAccountSuccess(token: string, uid: string, cid: string) {
    setAccessToken(token);
    setUserId(uid);
    setCustomerId(cid);
    setActiveStep(2);
  }

  function handleCloudNext() {
    setActiveStep(3); // → AutoFix step
  }

  function handleAutoFixNext() {
    setActiveStep(4); // → Payment
  }

  function handlePaymentNext() {
    setActiveStep(5);
  }

  async function handleScanNext(score: number, count: number) {
    setRiskScore(score);
    setFindingCount(count);
    if (sessionId) {
      try { await axios.post(`${API_BASE}/onboarding/${sessionId}/complete`); } catch {}
    }
    setActiveStep(6);
  }

  const planName = plan.charAt(0).toUpperCase() + plan.slice(1);
  const progress = (activeStep / (STEP_LABELS.length - 1)) * 100;

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Resuming your session…</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0d1117 0%, #0a1929 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 2.5, pb: 1, textDecoration: 'none' }}>
        <SecurityIcon sx={{ color: '#00E5FF', fontSize: 28, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
        <Typography variant="h6" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ height: 3, bgcolor: 'rgba(255,255,255,0.06)',
          '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }}
      />

      {/* Stepper */}
      <Box sx={{ px: { xs: 2, sm: 6 }, pt: 3, pb: 1, display: { xs: 'none', sm: 'block' } }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      {/* Mobile step indicator */}
      <Typography variant="caption" color="text.secondary" textAlign="center"
        sx={{ display: { xs: 'block', sm: 'none' }, py: 1 }}>
        Step {activeStep + 1} of {STEP_LABELS.length} — {STEP_LABELS[activeStep]}
      </Typography>

      {/* Content */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', p: { xs: 2, sm: 4 } }}>
        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, sm: 5 },
            width: '100%',
            maxWidth: activeStep === 2 || activeStep === 3 ? 720 : 640,
            borderRadius: 3,
            border: '1px solid rgba(0,180,216,0.1)',
            position: 'relative',
          }}
        >
          {activeStep === 0 && (
            <StepPlan selected={plan} onSelect={setPlan} onNext={handlePlanNext} />
          )}
          {activeStep === 1 && sessionId && (
            <StepAccount sessionId={sessionId} planName={planName} onSuccess={handleAccountSuccess} />
          )}
          {activeStep === 2 && sessionId && (
            <StepCloud
              sessionId={sessionId}
              onNext={handleCloudNext}
            />
          )}
          {activeStep === 3 && sessionId && customerId && (
            <StepAutoFix
              sessionId={sessionId}
              customerId={customerId}
              onNext={handleAutoFixNext}
              onSkip={handleAutoFixNext}
            />
          )}
          {activeStep === 4 && sessionId && customerId && (
            <StepPayment
              sessionId={sessionId}
              customerId={customerId}
              accessToken={accessToken}
              plan={plan}
              planName={planName}
              priceMonthly={planPrice}
              trialDays={trialDays}
              onNext={handlePaymentNext}
            />
          )}
          {activeStep === 5 && sessionId && customerId && (
            <StepScan
              sessionId={sessionId}
              customerId={customerId}
              onNext={handleScanNext}
            />
          )}
          {activeStep === 6 && (
            <StepComplete
              plan={plan}
              riskScore={riskScore}
              findingCount={findingCount}
              connectedProvider={connectedProvider}
              sessionId={sessionId}
              accessToken={accessToken}
              trialDays={trialDays}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
}
